// #region Imports
import { TextArea } from '@radix-ui/themes';
import { useFormContext } from 'react-hook-form';
import './AZFeedback.css';
// #endregion

// #region Types
interface FormPartProps {
  title: string;
  description: string;
  name: string;
  required?: boolean;
  commentDescription: string;
  lowerNoteDescription: string;
  upperNoteDescription: string;
}
// #endregion

// #region Component
const FormPart = ({
  title,
  description,
  name,
  required = true,
  commentDescription,
  lowerNoteDescription,
  upperNoteDescription,
}: FormPartProps) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const selectedValue = watch(name) as number | undefined;
  const commentName = `${name}Comment`;

  return (
    <div>
      {/* #region NPS title */}
      <p className="az-feedback-label" style={{ color: 'var(--az-palette-ice-white)' }}>
        {title}
      </p>
      <p className="az-feedback-disclaimer">{description}</p>
      {/* #endregion */}

      {/* #region NPS buttons */}
      <div className="az-feedback-nps-container" role="group" aria-label={`${title} score`}>
        {Array.from({ length: 11 }, (_, i) => i).map((value) => (
          <button
            key={value}
            type="button"
            className="az-feedback-nps-btn"
            aria-label={`${value}`}
            aria-pressed={selectedValue === value}
            data-selected={selectedValue === value || undefined}
            onClick={() => setValue(name, value, { shouldValidate: true })}
          >
            {value}
          </button>
        ))}
      </div>
      <input
        type="hidden"
        {...register(name, {
          required: required ? 'Please select a score' : false,
          validate: (v) =>
            !required || (v !== undefined && v !== null && v !== '')
              ? true
              : 'Please select a score',
        })}
      />

      {errors[name] && (
        <p className="az-feedback-error-msg" role="alert">
          {String(errors[name]?.message ?? 'Required')}
        </p>
      )}
      {/* #endregion */}

      {/* #region NPS labels */}
      <div className="az-feedback-nps-labels" aria-hidden="true">
        <span>{lowerNoteDescription}</span>
        <span>{upperNoteDescription}</span>
      </div>
      {/* #endregion */}

      {/* #region Comment textarea */}
      <div>
        <label className="az-feedback-label" htmlFor={commentName}>
          {commentDescription}
          {required ? '' : ' (optional)'}
        </label>
        <TextArea
          id={commentName}
          placeholder={commentDescription}
          {...register(commentName, {
            required: required ? 'This field is required' : false,
          })}
        />
        {errors[commentName] && (
          <p className="az-feedback-error-msg" role="alert">
            {String(errors[commentName]?.message ?? 'Required')}
          </p>
        )}
      </div>
      {/* #endregion */}
    </div>
  );
};
// #endregion

// #region Display Name
FormPart.displayName = 'FormPart';
// #endregion

export { FormPart };
